import React, { useEffect, useState } from "react";
import { X, Camera, Send } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { fileToBase64 } from "@/lib/files";
import { Spinner } from "@/components/kit";

export default function ContactModal({ open, onClose, defaultName }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName || "");
      setMessage("");
      setPhoto(null);
    }
  }, [open, defaultName]);

  if (!open) return null;

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(await fileToBase64(file));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() && !photo) {
      toast.error("Add a message or a photo first.");
      return;
    }
    setSending(true);
    try {
      await apiFetch("/growth/contact", {
        method: "POST",
        body: JSON.stringify({ name, message, photo_base64: photo }),
      });
      toast.success("Thanks! Your message is on its way. 🐾");
      onClose();
    } catch (err) {
      toast.error(err.message || "Couldn't send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="contact-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#F0ECE4]">
          <h2 className="font-display text-xl font-extrabold">Get in touch</h2>
          <button onClick={onClose} data-testid="contact-close" className="h-9 w-9 rounded-full bg-[#F2EFE9] flex items-center justify-center">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-sm font-bold text-[#4A4746]">Your name</label>
            <input
              data-testid="contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full mt-1.5 px-4 py-3 rounded-2xl bg-[#F2EFE9] font-semibold outline-none focus:ring-2 focus:ring-[#E86A45]"
            />
          </div>
          <div>
            <label className="text-sm font-bold text-[#4A4746]">Message</label>
            <textarea
              data-testid="contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Suggestion, comment, support need — or tell us about your pup!"
              className="w-full mt-1.5 px-4 py-3 rounded-2xl bg-[#F2EFE9] font-semibold outline-none focus:ring-2 focus:ring-[#E86A45] resize-none"
            />
          </div>
          <div>
            <label className="text-sm font-bold text-[#4A4746]">Photo of your pup (optional)</label>
            {photo ? (
              <div className="relative mt-1.5 inline-block">
                <img src={photo} alt="upload" className="h-24 w-24 rounded-2xl object-cover" />
                <button type="button" onClick={() => setPhoto(null)} data-testid="contact-photo-remove" className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-[#2D2B2A] text-white flex items-center justify-center">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label data-testid="contact-photo-btn" className="mt-1.5 cursor-pointer flex items-center gap-2 w-fit px-4 py-2.5 rounded-full bg-[#F2EFE9] font-bold text-sm text-[#4A4746] hover:bg-[#E8E3DA]">
                <Camera size={16} /> Add photo
                <input type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
              </label>
            )}
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-[#6F8F72] text-white font-extrabold py-3.5 hover:bg-[#61805f] disabled:opacity-60 transition-colors"
          >
            {sending ? <Spinner size={20} className="!border-white/40 !border-t-white" /> : <><Send size={17} /> Send message</>}
          </button>
        </form>
      </div>
    </div>
  );
}
